import { Box, Button, FormControl, TextField, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import {
  getMockOutlinedInputSx,
  getMockOutlinedLabelSx,
  resolveSxProps,
} from 'utility/styles';
import { DynamicFieldChip } from '../components/DynamicFieldChip';
import { DynamicFieldType, TDynamicFieldOnChange } from '../DynamicFormField';
import { getFieldLabel } from '../utility/functions';

/**
 * ---------------------------------------
 * Multitext Dynamic Field
 * Dynamic Fields Spec: Multitext Fields
 * ---------------------------------------
 *
 * Renders a "multitext" dynamic field - a string array field.
 *   [text input] [Add]
 *   (chips) ...
 *
 * Prevents empty strings and duplicate entries.
 * Supports add via button or Enter key, remove via chip.
 */

type MultitextDynamicFieldProps = {
  fieldConfig: DynamicFieldType;
  selectedValue: any;
  disabled: boolean;
  handleValueChange: TDynamicFieldOnChange;
  fieldSx?: SxProps<Theme>;
};

export const MultitextDynamicField = memo(
  ({
    fieldConfig,
    selectedValue,
    disabled,
    handleValueChange,
    fieldSx,
  }: MultitextDynamicFieldProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [inputText, setInputText] = useState('');

    // Translation strings
    const strAdd = t('blox.actions.add');
    const strNoEntries = t('blox.general.no_entries');
    const strEnterValue = t('blox.actions.enter_a_value');
    const strEnterValuePlaceholder = `${strEnterValue}...`;

    // Refs to elements for focus management
    const textInputRef = useRef<HTMLInputElement>(null);

    // Clean string label for mui outlined
    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    const fieldName = useMemo(() => {
      return fieldConfig?.name ?? '';
    }, [fieldConfig?.name]);

    // Normalize and ensure string[]
    // handlers expect string array
    const strValues = useMemo<string[]>(() => {
      if (!selectedValue) {
        return [];
      }
      if (Array.isArray(selectedValue)) {
        const mapped = selectedValue.map((v) => String(v));
        return mapped;
      }
      return [];
    }, [selectedValue]);

    const hasEntries = strValues.length > 0;
    const isInputEmpty = !inputText.trim();

    // Cleans up values and sends to parent onChange
    const dispatchChange = useCallback(
      (newValues: string[]) => {
        const result = newValues.map((v) => v.trim()).filter(Boolean);
        handleValueChange(fieldName, result);
      },
      [handleValueChange, fieldName],
    );

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(e.target.value);
    }, []);

    const handleAdd = useCallback(() => {
      // Refocus input for smoother multiple entry
      if (textInputRef?.current) {
        textInputRef?.current?.focus();
      }

      const trimmed = inputText.trim();

      // Reject empty or duplicate and clear input
      if (!trimmed || strValues.includes(trimmed)) {
        setInputText('');
        return;
      }

      // push value and clear input
      const newValues = [...strValues, trimmed];
      dispatchChange(newValues);
      setInputText('');
    }, [inputText, strValues, dispatchChange]);

    const handleRemoveChip = useCallback(
      (val: string) => {
        dispatchChange(strValues.filter((v) => v !== val));
      },
      [strValues, dispatchChange],
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleAdd();
        }
      },
      [handleAdd],
    );

    /**
     * Mock MUI Styles
     */

    const mockOutlinedSx = useMemo(() => getMockOutlinedInputSx(theme, false), [theme]);
    const mockLabelSx = useMemo(() => getMockOutlinedLabelSx(theme), [theme]);

    // Resolve the top level fieldSx to object
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    const wrapperSx: SxProps<Theme> = useMemo(
      () => ({
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        mt: 2,
        mb: 0.5,
        pt: 1.5,
        pb: 1,
        px: 1,
        borderRadius: 1,
        ...mockOutlinedSx,
        ...resolvedSx,
      }),
      [mockOutlinedSx, resolvedSx],
    );

    return (
      <FormControl
        data-testid={`dynamic-field-multitext-${fieldName}`}
        sx={wrapperSx}
        disabled={disabled}
        margin="none"
        variant="outlined"
      >
        <Typography
          data-testid={`multitext-label-${fieldName}`}
          sx={mockLabelSx}
        >
          {fieldLabel}
        </Typography>

        <Box
          data-testid={`multitext-chips-container-${fieldName}`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            pt: 1,
          }}
        >
          {!hasEntries ? (
            <Typography
              data-testid={`multitext-chips-empty-${fieldName}`}
              sx={{
                fontSize: bloxValues.smallFontSizeRem,
                color: theme.palette.text.disabled,
                py: 0.5,
              }}
            >
              {strNoEntries}
            </Typography>
          ) : (
            <Fragment>
              {strValues.map((val, idx) => {
                const keyId = `key-${val}-${idx}`;
                return (
                  <DynamicFieldChip
                    key={keyId}
                    idTag="multitext"
                    value={val}
                    label={val}
                    onRemove={handleRemoveChip}
                    disabled={disabled}
                  />
                );
              })}
            </Fragment>
          )}
        </Box>

        {/* Input + add button */}
        <Box
          data-testid={`multitext-input-area-${fieldName}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <TextField
            label={null}
            size="small"
            variant="outlined"
            placeholder={strEnterValuePlaceholder}
            disabled={disabled}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            fullWidth={true}
            autoComplete="off"
            inputRef={textInputRef}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: bloxValues.smallFontSizeRem,
                display: 'flex',
                alignItems: 'center',
                p: 1,
              },
            }}
            slotProps={{
              htmlInput: {
                'data-testid': `multitext-input-${fieldName}`,
              },

              inputLabel: {
                shrink: true,
              },
            }}
          />
          <Button
            data-testid={`multitext-add-${fieldName}`}
            variant="outlined"
            size="small"
            disabled={disabled || isInputEmpty}
            onClick={handleAdd}
            disableElevation={true}
            sx={{
              // Align approx with textfield
              p: 1,
              height: 36,
              width: 80,
              mt: '4px',
            }}
          >
            {strAdd}
          </Button>
        </Box>
      </FormControl>
    );
  },
);
MultitextDynamicField.displayName = 'MultitextFieldRenderer';
